import { useMessagesStore } from "@/atoms/playground";
import {
  CONFIDENTIAL_ROUTE_ID,
  useConfidentialSessionStore,
} from "@/atoms/confidential-session";
import {
  fetchStreamData,
  StreamRequestError,
} from "@/pages/Playground/components/fetcher";

import { ChatFormData, StreamAction, StreamActionItem } from "@/types";
import { conversationsApi } from "@/services/conversation-service";
import { createMessage } from "@/utils/chat/create-message";
import { parseStreamError } from "@/utils/chat/parsers";
import { preserveStreamedThinking } from "@/utils/chat/preserve-streamed-thinking";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { usePlaygroundRouting } from "@/pages/Playground/routing/playground-routing";
import { useConversationScope } from "@/hooks/useConversationScope";
import {
  conversationQueryKeys,
  conversationScopeIdentity,
  getConversationRequestScope,
  type ConversationRequestScope,
  type ConversationScope,
} from "@/features/playground/conversation-query-keys";

type StreamOperation = {
  token: symbol;
  conversationId: string;
  confidential: boolean;
  scopeIdentity: string;
  requestScope?: ConversationRequestScope;
  queryKey: readonly unknown[];
};

export const useStream = () => {
  const {
    setMessages,
    pushReference,
    references,
    conversationScopeIdentity: messageScopeIdentity,
    agentSteps: allAgentSteps,
    setAgentSteps,
    streamActions: allStreamActions,
    setStreamActions,
  } = useMessagesStore();
  const queryClient = useQueryClient();
  const { id, routeNamespace } = usePlaygroundRouting();
  const { scope, identity } = useConversationScope();
  const currentScopeIdentityRef = useRef(identity);
  useLayoutEffect(() => {
    currentScopeIdentityRef.current = identity;
  }, [identity]);
  const confidentialSession = useConfidentialSessionStore(
    (state) => state.session,
  );
  const activeConversationId =
    id === CONFIDENTIAL_ROUTE_ID ? confidentialSession?.id : id;

  const canShowScopedStreamState =
    id === CONFIDENTIAL_ROUTE_ID || messageScopeIdentity === identity;
  const agentSteps = activeConversationId && canShowScopedStreamState
    ? allAgentSteps[activeConversationId]
    : undefined;
  const streamActions = activeConversationId && canShowScopedStreamState
    ? allStreamActions[activeConversationId]
    : undefined;
  const [isPlanning, setIsPlanning] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [activeStreams, setActiveStreams] = useState<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const streamOperationsRef = useRef<Map<string, StreamOperation>>(new Map());
  const latestOperationTokensRef = useRef<Map<string, symbol>>(new Map());
  const messageFlushFrameRef = useRef<number | null>(null);
  const pendingMessagesRef = useRef<any[] | null>(null);
  const pendingQueryUpdateRef = useRef<{
    operation: StreamOperation;
    messages: any[];
  } | null>(null);

  const isOperationCurrent = useCallback((operation: StreamOperation) => {
    const activeOperation = streamOperationsRef.current.get(
      operation.conversationId,
    );
    return (
      activeOperation?.token === operation.token &&
      (operation.confidential ||
        currentScopeIdentityRef.current === operation.scopeIdentity)
    );
  }, []);

  const isLatestOperationInScope = useCallback(
    (operation: StreamOperation) =>
      latestOperationTokensRef.current.get(operation.conversationId) ===
        operation.token &&
      (operation.confidential ||
        currentScopeIdentityRef.current === operation.scopeIdentity),
    [],
  );

  const flushPendingMessages = useCallback(() => {
    const pendingMessages = pendingMessagesRef.current;
    const pendingQuery = pendingQueryUpdateRef.current;

    messageFlushFrameRef.current = null;
    pendingMessagesRef.current = null;
    pendingQueryUpdateRef.current = null;

    const operationIsCurrent =
      pendingQuery && isOperationCurrent(pendingQuery.operation);

    if (
      pendingMessages &&
      pendingQuery &&
      operationIsCurrent &&
      (window.location.pathname.includes(
        pendingQuery.operation.conversationId,
      ) ||
        (window.location.pathname.endsWith(`/${CONFIDENTIAL_ROUTE_ID}`) &&
          useConfidentialSessionStore.getState().session?.id ===
            pendingQuery.operation.conversationId))
    ) {
      startTransition(() => {
        if (pendingQuery.operation.confidential) {
          setMessages([...pendingMessages]);
        } else {
          useMessagesStore.getState().setConversationState(
            pendingQuery.operation.scopeIdentity,
            [...pendingMessages],
            useMessagesStore.getState().references,
          );
        }
      });
    }

    if (pendingQuery && operationIsCurrent) {
      queryClient.setQueryData(pendingQuery.operation.queryKey, (old: any) => {
        if (!old) return old;
        return { ...old, messages: [...pendingQuery.messages] };
      });
    }
  }, [isOperationCurrent, queryClient, setMessages]);

  const scheduleMessagesFlush = useCallback(
    (operation: StreamOperation, messagesToFlush: any[]) => {
      if (!isOperationCurrent(operation)) return;
      pendingMessagesRef.current = messagesToFlush;
      pendingQueryUpdateRef.current = {
        operation,
        messages: messagesToFlush,
      };

      if (messageFlushFrameRef.current !== null) {
        return;
      }

      messageFlushFrameRef.current = window.requestAnimationFrame(() => {
        flushPendingMessages();
      });
    },
    [flushPendingMessages, isOperationCurrent],
  );

  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    mountedRef.current = true;
    const operations = streamOperationsRef.current;
    const tokens = latestOperationTokensRef.current;
    const controllers = abortControllersRef.current;
    return () => {
      mountedRef.current = false;
      // Invalidate ownership before abort rejection callbacks can run.
      operations.clear();
      tokens.clear();
      controllers.forEach(controller => controller.abort());
      controllers.clear();
      pendingMessagesRef.current = null;
      pendingQueryUpdateRef.current = null;
      if (messageFlushFrameRef.current !== null) {
        window.cancelAnimationFrame(messageFlushFrameRef.current);
        messageFlushFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelledCurrentScopeStream = false;

    streamOperationsRef.current.forEach((operation, conversationId) => {
      if (operation.confidential || operation.scopeIdentity === identity) {
        return;
      }

      cancelledCurrentScopeStream = true;
      abortControllersRef.current.get(conversationId)?.abort();
      abortControllersRef.current.delete(conversationId);
      streamOperationsRef.current.delete(conversationId);
      setAgentSteps(conversationId, undefined);
      setStreamActions(conversationId, undefined);
    });

    const pendingOperation = pendingQueryUpdateRef.current?.operation;
    if (
      pendingOperation &&
      !pendingOperation.confidential &&
      pendingOperation.scopeIdentity !== identity
    ) {
      if (messageFlushFrameRef.current !== null) {
        window.cancelAnimationFrame(messageFlushFrameRef.current);
      }
      messageFlushFrameRef.current = null;
      pendingMessagesRef.current = null;
      pendingQueryUpdateRef.current = null;
    }

    if (cancelledCurrentScopeStream) {
      setActiveStreams(new Set(streamOperationsRef.current.keys()));
      setIsPlanning(false);
      setIsThinking(false);
    }
  }, [identity, setAgentSteps, setStreamActions]);

  const isStreaming = useCallback(
    (conversationId: string) => activeStreams.has(conversationId),
    [activeStreams],
  );

  const stop = useCallback(
    (conversationId?: string) => {
      const targetId = conversationId || id;
      if (!targetId) return;

      const controller = abortControllersRef.current.get(targetId);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(targetId);
        streamOperationsRef.current.delete(targetId);
        setActiveStreams((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
        if (targetId === id) {
          setIsPlanning(false);
          setIsThinking(false);
        }
      }
    },
    [id],
  );

  const hasOpenThinkTag = (content: string) => {
    const opens = (content.match(/<think>/g) || []).length;
    const closes = (content.match(/<\/think>/g) || []).length;
    return opens > closes;
  };

  const parseMarkdownLink = (text: string) => {
    const match = /^\[([\s\S]+?)\]\(([^)]*)\)$/.exec(text.trim());
    if (match) {
      return { label: match[1], url: match[2] };
    }
    return { label: text };
  };

  const getStreamActions = (conversationId: string) =>
    useMessagesStore.getState().streamActions[conversationId] || [];

  const completeRunningActions = (conversationId: string) => {
    const actions = getStreamActions(conversationId);
    if (!actions.some((action) => action.status === "running")) return;
    setStreamActions(
      conversationId,
      actions.map((action) =>
        action.status === "running"
          ? { ...action, status: "completed" as const }
          : action,
      ),
    );
  };

  const handleToolUseEvent = (conversationId: string, obj: any) => {
    const actions = getStreamActions(conversationId);

    if (obj.phase === "start") {
      let detail: string | undefined;
      if (typeof obj.input === "string") {
        detail = obj.input;
      } else if (obj.input && typeof obj.input === "object") {
        detail = Object.values(obj.input).find(
          (value): value is string => typeof value === "string",
        );
      }
      const newAction: StreamAction = {
        id: obj.id,
        name: obj.name || "",
        icon: obj.icon,
        status: "running",
        detail,
        items: [],
      };
      setStreamActions(conversationId, [
        ...actions.map((action) =>
          action.status === "running"
            ? { ...action, status: "completed" as const }
            : action,
        ),
        newAction,
      ]);
      return;
    }

    if (obj.phase === "processing") {
      const { label, url } = parseMarkdownLink(obj.name || "");
      const item: StreamActionItem = {
        id: `${obj.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        kind: "processing",
        label,
        url: url || (typeof obj.input === "string" ? obj.input : undefined),
        icon: obj.icon,
      };

      const existing = actions.find((action) => action.id === obj.id);
      if (existing) {
        setStreamActions(
          conversationId,
          actions.map((action) =>
            action.id === obj.id
              ? { ...action, items: [...action.items, item] }
              : action,
          ),
        );
      } else {
        // processing without a start event: create the action on the fly
        setStreamActions(conversationId, [
          ...actions,
          {
            id: obj.id,
            name: label,
            icon: obj.icon,
            status: "running",
            items: [],
          },
        ]);
      }
      return;
    }

    // any terminal phase (end / completed / error) closes the action
    setStreamActions(
      conversationId,
      actions.map((action) =>
        action.id === obj.id
          ? { ...action, status: "completed" as const }
          : action,
      ),
    );
  };

  const handleCompactionEvent = (conversationId: string, obj: any) => {
    const actions = getStreamActions(conversationId);
    const item: StreamActionItem = {
      id: `compaction-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: "compaction",
      label: obj.data?.tool || "",
      detail: obj.data?.tokens,
    };

    let targetIndex = -1;
    let lastRunningIndex = -1;
    for (let i = actions.length - 1; i >= 0; i--) {
      if (actions[i].status !== "running") continue;
      if (lastRunningIndex === -1) lastRunningIndex = i;
      if (!obj.data?.tool || actions[i].name === obj.data.tool) {
        targetIndex = i;
        break;
      }
    }
    const fallbackIndex = targetIndex !== -1 ? targetIndex : lastRunningIndex;

    if (fallbackIndex !== -1) {
      setStreamActions(
        conversationId,
        actions.map((action, index) =>
          index === fallbackIndex
            ? { ...action, items: [...action.items, item] }
            : action,
        ),
      );
    } else {
      setStreamActions(conversationId, [
        ...actions,
        {
          id: item.id,
          name: obj.data?.tool || "compaction",
          status: "completed",
          items: [item],
        },
      ]);
    }
  };

  const { mutate: streamMessage, isPending } = useMutation({
    mutationFn: async ({
      data,
      conversation_id,
      confidential = false,
      operationScope: requestedOperationScope,
    }: {
      data: ChatFormData;
      conversation_id: string;
      confidential?: boolean;
      operationScope?: ConversationScope;
    }) => {
      if (!mountedRef.current || streamOperationsRef.current.has(conversation_id)) {
        return;
      }

      const operationScope = requestedOperationScope ?? scope;
      const scopeIdentity = conversationScopeIdentity(operationScope);
      const operationRequestScope = confidential
        ? undefined
        : getConversationRequestScope(operationScope);

      if (!confidential && currentScopeIdentityRef.current !== scopeIdentity) {
        return;
      }

      setAgentSteps(conversation_id, undefined);
      setStreamActions(conversation_id, undefined);
      setIsPlanning(false);
      setIsThinking(false);

      const controller = new AbortController();
      abortControllersRef.current.set(conversation_id, controller);
      const operation: StreamOperation = {
        token: Symbol(conversation_id),
        conversationId: conversation_id,
        confidential,
        scopeIdentity,
        requestScope: operationRequestScope,
        queryKey: confidential
          ? ["conversation", conversation_id, routeNamespace]
          : conversationQueryKeys.detail(
              operationScope,
              conversation_id,
              routeNamespace,
            ),
      };
      streamOperationsRef.current.set(conversation_id, operation);
      latestOperationTokensRef.current.set(conversation_id, operation.token);
      setActiveStreams((prev) => new Set(prev).add(conversation_id));

      const streamingPayload = {
        message: data.message,
        base_llm_config: {
          id: data.llm_config_id,
          temperature: data.temperature,
          max_tokens: data.max_tokens,
        },
        ...(data.mode !== "agentic"
          ? {
              planner_llm_config: {
                id: data.planner_llm_config,
                temperature: data.temperature,
                max_tokens: data.max_tokens,
              },
            }
          : {}),
        metadata: {
          tools: data.metadata?.tools || [],
          language: data.metadata?.language || "ar",
        },
        mode: data.mode,
      };
      const messageState = useMessagesStore.getState();
      const updated = [
        ...(confidential ||
        messageState.conversationScopeIdentity === operation.scopeIdentity
          ? messageState.messages
          : []),
      ];
      const newMessage = createMessage({
        content: data.message,
        conversation_id,
        metadata: data.metadata,
      });
      updated.push(newMessage);
      if (confidential) {
        setMessages(updated);
      } else {
        messageState.setConversationState(
          operation.scopeIdentity,
          updated,
          messageState.references,
        );
      }

      const queryKey = operation.queryKey;
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return { ...old, messages: updated };
      });

      let buffer = "";
      let currentContent = "";
      let inThinkingBlock = false;
      const closeThinkingBlock = () => {
        if (inThinkingBlock) {
          currentContent += "</think>";
          inThinkingBlock = false;
        }
      };
      const finalizeThinking = () => {
        if (!isOperationCurrent(operation)) return;
        const wasOpen = inThinkingBlock;
        closeThinkingBlock();
        setIsThinking(false);
        if (wasOpen) {
          const lastIndex = updated.length - 1;
          if (lastIndex >= 0 && updated[lastIndex].role === "assistant") {
            updated[lastIndex] = {
              ...updated[lastIndex],
              content: currentContent,
            };
            scheduleMessagesFlush(operation, [...updated]);
          }
        }
      };

      try {
        const stream = await fetchStreamData(
          conversation_id,
          streamingPayload,
          controller.signal,
          operation.requestScope,
        );

        for await (const chunk of stream) {
          if (!isOperationCurrent(operation)) {
            controller.abort();
            return data;
          }
          buffer += chunk;

          while (true) {
            const startIndex = buffer.indexOf("{");
            if (startIndex === -1) {
              if (buffer.includes("[DONE]")) {
                finalizeThinking();
                return data;
              }
              break;
            }

            let balance = 0;
            let endIndex = -1;
            let inString = false;
            let escape = false;

            for (let i = startIndex; i < buffer.length; i++) {
              const char = buffer[i];
              if (escape) {
                escape = false;
                continue;
              }
              if (char === "\\") {
                escape = true;
                continue;
              }
              if (char === '"') {
                inString = !inString;
                continue;
              }
              if (!inString) {
                if (char === "{") balance++;
                else if (char === "}") {
                  balance--;
                  if (balance === 0) {
                    endIndex = i;
                    break;
                  }
                }
              }
            }

            if (endIndex !== -1) {
              const jsonStr = buffer.substring(startIndex, endIndex + 1);
              buffer = buffer.substring(endIndex + 1);

              try {
                const obj = JSON.parse(jsonStr);

                if (!isOperationCurrent(operation)) {
                  controller.abort();
                  return data;
                }

                if (obj.event === "title_generated") {
                  if (confidential) {
                    useConfidentialSessionStore.getState().setTitle(obj.data);
                  } else {
                    queryClient.invalidateQueries({
                      queryKey: conversationQueryKeys.lists(operationScope),
                    });
                    queryClient.setQueryData(queryKey, (old: any) => {
                      if (!old) return old;
                      return { ...old, name: obj.data };
                    });
                  }
                }
                if (obj.event === "error") {
                  closeThinkingBlock();
                  currentContent += parseStreamError(obj.message).message;
                  setIsPlanning(false);
                  setIsThinking(false);
                } else if (obj.event === "heartbeat") {
                  if (data.mode === "simple") {
                    setIsThinking(true);
                  }
                } else if (obj.event === "thinking") {
                  if (!inThinkingBlock) {
                    currentContent += "<think>";
                    inThinkingBlock = true;
                  }
                  currentContent += obj.data;
                  setIsThinking(true);
                } else if (obj.event === "content") {
                  closeThinkingBlock();
                  currentContent += obj.data;
                  setIsThinking(hasOpenThinkTag(currentContent));
                  completeRunningActions(conversation_id);
                } else if (obj.event === "tool_use") {
                  handleToolUseEvent(conversation_id, obj);
                } else if (obj.event === "compaction") {
                  handleCompactionEvent(conversation_id, obj);
                } else if (obj.event === "finalize") {
                  closeThinkingBlock();
                  setIsThinking(false);
                  completeRunningActions(conversation_id);
                  if (obj.status === "FAILED") {
                    currentContent += obj.message_preview;
                  }
                } else if (obj.event === "planner_start") {
                  setIsPlanning(true);
                  setIsThinking(false);
                } else if (obj.event === "plan_generated") {
                  setIsPlanning(false);
                  setIsThinking(false);
                  const current =
                    useMessagesStore.getState().agentSteps[conversation_id];
                  setAgentSteps(conversation_id, {
                    ...current,
                    steps: obj.data.steps,
                  } as any);
                  console.log(obj.data.steps);
                } else if (obj.event === "reference") {
                  const newReferences = {
                    ...references,
                    [obj.data.type]: {
                      ...references?.[obj.data.type],
                      [obj.data.id]: obj.data,
                    },
                  };
                  pushReference(newReferences);
                } else if (obj.event === "step_update" && obj.data.id) {
                  const prev =
                    useMessagesStore.getState().agentSteps[conversation_id];
                  if (prev) {
                    const steps = prev.steps.map((step) => {
                      if (step.id === parseInt(obj.data.id)) {
                        return { ...step, status: obj.data.status };
                      }
                      return step;
                    });
                    setAgentSteps(conversation_id, {
                      ...prev,
                      steps,
                      currentStep: parseInt(obj.data.id),
                    });
                  }
                } else if (obj.event === "query_end") {
                  completeRunningActions(conversation_id);
                  const prev =
                    useMessagesStore.getState().agentSteps[conversation_id];
                  if (prev) {
                    const steps = prev.steps.map((step) => {
                      if (step.status === "running") {
                        return { ...step, status: "completed" as const };
                      }
                      return step;
                    });
                    setAgentSteps(conversation_id, { ...prev, steps });
                  }
                }

                const lastIndex = updated.length - 1;
                if (lastIndex >= 0 && updated[lastIndex].role === "assistant") {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    content: currentContent,
                  };
                } else {
                  const newMessage = createMessage({
                    role: "assistant",
                    content: currentContent,
                    conversation_id,
                  });
                  updated.push(newMessage);
                }
                scheduleMessagesFlush(operation, [...updated]);
              } catch (error) {
                console.log("Stream parsing error:", error);
              }
            } else {
              break;
            }
          }
        }
        finalizeThinking();
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Stream aborted");
          if (isOperationCurrent(operation)) {
            finalizeThinking();
            setAgentSteps(conversation_id, undefined);
            setStreamActions(conversation_id, undefined);
            setIsPlanning(false);
            setIsThinking(false);
          }
        } else if (
          confidential &&
          error instanceof StreamRequestError &&
          (error.status === 404 || error.status === 410)
        ) {
          useConfidentialSessionStore.getState().expire();
        } else if (isOperationCurrent(operation)) {
          // console.error("Stream error:", error);

          toast.error(
            typeof error.message === "string"
              ? error.message
              : "Failed to stream response. Please try again.",
          );
        }
      } finally {
        const operationIsCurrent = isOperationCurrent(operation);
        if (messageFlushFrameRef.current !== null && operationIsCurrent) {
          window.cancelAnimationFrame(messageFlushFrameRef.current);
          flushPendingMessages();
        }
        if (
          streamOperationsRef.current.get(conversation_id)?.token ===
          operation.token
        ) {
          abortControllersRef.current.delete(conversation_id);
        }
        if (!confidential && operationIsCurrent) {
          // Refresh persisted IDs in the background without delaying the next chat turn.
          const lastTemporaryId = updated[updated.length - 1]?.id;
          const persistenceRefresh = conversationsApi
            .getConversation(conversation_id, operation.requestScope)
            .then((saved: any) => {
              if (!isLatestOperationInScope(operation)) return;
              const cached = queryClient.getQueryData<any>(queryKey);
              if (
                abortControllersRef.current.has(conversation_id) ||
                cached?.messages?.[cached.messages.length - 1]?.id !==
                  lastTemporaryId ||
                !saved.messages ||
                saved.messages.length < updated.length
              )
                return;
              const messagesWithThinking = preserveStreamedThinking(
                saved.messages,
                updated,
              );
              const savedWithThinking = {
                ...saved,
                messages: messagesWithThinking,
              };
              queryClient.setQueryData(queryKey, savedWithThinking);
              const currentState = useMessagesStore.getState();
              const current = currentState.messages;
              if (
                currentState.conversationScopeIdentity ===
                  operation.scopeIdentity &&
                current[current.length - 1]?.id === lastTemporaryId &&
                window.location.pathname.includes(conversation_id)
              )
                currentState.setConversationState(
                  operation.scopeIdentity,
                  messagesWithThinking,
                  currentState.references,
                );
            })
            .catch(() => {
              // Keep the completed response visible if persistence refresh fails.
            });
          void persistenceRefresh.finally(() => {
            if (
              latestOperationTokensRef.current.get(conversation_id) ===
              operation.token
            ) {
              latestOperationTokensRef.current.delete(conversation_id);
            }
          });
        } else if (
          latestOperationTokensRef.current.get(conversation_id) ===
          operation.token
        ) {
          latestOperationTokensRef.current.delete(conversation_id);
        }

        if (
          streamOperationsRef.current.get(conversation_id)?.token ===
          operation.token
        ) {
          streamOperationsRef.current.delete(conversation_id);
          setActiveStreams((prev) => {
            const next = new Set(prev);
            next.delete(conversation_id);
            return next;
          });
        }
      }
      return data;
    },
  });
  const reset = useCallback(() => {
    setIsPlanning(false);
    setIsThinking(false);
  }, []);

  return {
    streamMessage,
    agentSteps,
    streamActions,
    isPending,
    isPlanning,
    isThinking,
    reset,
    stop,
    isStreaming,
  };
};
